import React, { useEffect, useRef, useState } from 'react';
import { Container, Typography, Grid, Card, CardContent, Button, Box, Chip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import ComputerIcon from '@mui/icons-material/Computer';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import RouterIcon from '@mui/icons-material/Router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchIcon from '@mui/icons-material/Search';
import HandymanIcon from '@mui/icons-material/Handyman';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';

// ── Keyframe animations ──────────────────────────────────────────────────────

const rotateCW = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const rotateCCW = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(-360deg); }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 25px rgba(255,107,107,0.25), inset 0 0 20px rgba(255,107,107,0.06);
    border-color: rgba(255,107,107,0.35);
  }
  50% {
    box-shadow: 0 0 60px rgba(255,107,107,0.5), 0 0 100px rgba(255,107,107,0.2),
                inset 0 0 40px rgba(255,107,107,0.12);
    border-color: rgba(255,107,107,0.7);
  }
`;

const ringPulse = keyframes`
  0%, 100% { border-color: rgba(255,107,107,0.13); }
  50%       { border-color: rgba(255,107,107,0.3); }
`;

const floatBadge = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-9px); }
`;

const badgeGlow = keyframes`
  0%, 100% { box-shadow: 0 4px 12px rgba(255,107,107,0.1); }
  50%       { box-shadow: 0 4px 20px rgba(255,107,107,0.3); }
`;

const rippleOut = keyframes`
  0%   { transform: scale(0.8);  opacity: 0.5; }
  100% { transform: scale(1.55); opacity: 0;   }
`;

const scaleBreath = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.07); }
`;

const iconFloat = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
    filter: drop-shadow(0 0 10px rgba(255,107,107,0.5));
  }
  50% {
    transform: translateY(-6px) scale(1.1);
    filter: drop-shadow(0 0 24px rgba(255,107,107,0.95)) drop-shadow(0 0 44px rgba(255,217,61,0.4));
  }
`;

const orbitDotPulse = keyframes`
  0%, 100% { opacity: 0.7; box-shadow: 0 0 4px 1px rgba(255,107,107,0.7); }
  50%       { opacity: 1;   box-shadow: 0 0 10px 4px rgba(255,107,107,1), 0 0 20px 2px rgba(255,217,61,0.4); }
`;

const slideReveal = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const marqueeTicker = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

// ── Data ─────────────────────────────────────────────────────────────────────

const features = [
  {
    icon: ComputerIcon,
    title: '16+ Years Enterprise Experience',
    description: 'Supporting mission-critical IT at Société Générale, Nestlé, and Velonex Technologies',
  },
  {
    icon: BuildIcon,
    title: 'Hardware Repair & Support',
    description: 'Laptops, desktops, servers, and mobile devices — Tier 1-3 on-site and remote',
  },
  {
    icon: SecurityIcon,
    title: 'Cybersecurity — CySA+ Certified',
    description: 'CompTIA CySA+ | Zero Trust | Cisco ISE | Firewall configuration',
  },
  {
    icon: CloudIcon,
    title: 'Microsoft 365 & Azure',
    description: 'Azure AD, Exchange Online, SharePoint, Teams, Intune, and hybrid cloud',
  },
  {
    icon: RouterIcon,
    title: 'Network Engineering',
    description: 'LAN/WAN, VoIP, firewalls, wireless networks, and infrastructure setup',
  },
  {
    icon: StorageIcon,
    title: 'Windows, Linux & macOS',
    description: 'Multi-platform software deployment, server admin, and VMware/Proxmox virtualization',
  },
];

const whyChooseUs = [
  {
    icon: CheckCircleIcon,
    title: 'Certified IT Expert',
    description: 'CompTIA CySA+ certified with 16+ years of hands-on enterprise IT experience',
    link: '/about',
  },
  {
    icon: BuildIcon,
    title: 'Hardware & Repair',
    description: 'Tier 1-3 hardware and software support for laptops, desktops, servers, and mobile devices',
    link: '/laptop-repair',
  },
  {
    icon: CloudIcon,
    title: 'Microsoft & Cloud',
    description: 'Azure, Microsoft 365, Active Directory, and Intune — full cloud and hybrid administration',
    link: '/microsoft-365-cloud',
  },
  {
    icon: SecurityIcon,
    title: 'Cybersecurity',
    description: 'Zero Trust, firewall configuration, NAC, and endpoint security for your business',
    link: '/cybersecurity',
  },
];

const stats = [
  { value: 16,    suffix: '+', label: 'Years Experience' },
  { value: 500,   suffix: '+', label: 'Clients Served' },
  { value: 98,    suffix: '%', label: 'Satisfaction Rate' },
  { value: 24,    suffix: '/7', label: 'Support Available' },
];

const howItWorks = [
  {
    step: '01',
    icon: PhoneIcon,
    title: 'Contact Us',
    description: 'Call, email, or book online. Describe your issue and we\'ll confirm availability — usually same day.',
  },
  {
    step: '02',
    icon: SearchIcon,
    title: 'Diagnosis & Quote',
    description: 'We inspect your device or assess your IT setup and provide a transparent, no-surprise quote.',
  },
  {
    step: '03',
    icon: HandymanIcon,
    title: 'Expert Repair',
    description: 'Certified technician handles the work — hardware, software, or network — fast and correctly.',
  },
  {
    step: '04',
    icon: ThumbUpIcon,
    title: 'Guaranteed Results',
    description: 'You approve the outcome. All repairs come with a 90-day workmanship warranty.',
  },
];

const tickerItems = [
  'CompTIA CySA+ Certified',
  'Microsoft 365 Expert',
  'Azure & Entra ID',
  'Same-Day Repairs',
  '90-Day Warranty',
  'Lynn, MA Based',
  'Cisco ISE & NAC',
  'VMware & Proxmox',
  'Zero Trust Security',
  'Data Recovery',
];

const reviews = [
  { name: 'Sarah M.',    location: 'Lynn, MA',    rating: 5, text: 'Abderrazak fixed my MacBook screen same day. Fast, professional, and the price was very fair. Highly recommend!' },
  { name: 'James T.',    location: 'Saugus, MA',  rating: 5, text: 'Had a ransomware attack hit our small business. He cleaned everything up, secured our network, and set up proper backups. Lifesaver.' },
  { name: 'Maria L.',    location: 'Salem, MA',   rating: 5, text: 'Set up our entire Microsoft 365 environment — email, Teams, SharePoint. Everything works perfectly. Very knowledgeable.' },
  { name: 'David K.',    location: 'Peabody, MA', rating: 5, text: 'Recovered all my data from a dead hard drive. I thought everything was lost. Incredible work, very patient explaining the process.' },
  { name: 'Patricia R.', location: 'Lynn, MA',    rating: 5, text: 'Fixed my iPhone screen in under an hour. Used quality parts and gave a 90-day warranty. Won\'t go anywhere else.' },
  { name: 'Tom W.',      location: 'Revere, MA',  rating: 5, text: 'Set up our business WiFi and firewall from scratch. Network is rock solid now. On time, professional, fair pricing.' },
];

// ── Hooks ─────────────────────────────────────────────────────────────────────

function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ── Stat counter card ─────────────────────────────────────────────────────────

function StatCard({ value, suffix, label, started }) {
  const count = useCountUp(value, 1600, started);
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: '-0.02em' }}>
        {count}{suffix}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
}

// ── Reveal wrapper ─────────────────────────────────────────────────────────────

function Reveal({ children, delay = 0 }) {
  const [ref, inView] = useInView(0.15);
  return (
    <Box
      ref={ref}
      sx={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      {children}
    </Box>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

function Home() {
  const [statsRef, statsInView] = useInView(0.3);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* ── Hero ── */}
      <Box sx={{
        minHeight: '88vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', top: '15%', right: '8%',
          width: 420, height: 420,
          background: 'radial-gradient(circle, rgba(255,107,107,0.13) 0%, transparent 70%)',
          borderRadius: '50%', pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '10%', left: '2%',
          width: 280, height: 280,
          background: 'radial-gradient(circle, rgba(255,217,61,0.07) 0%, transparent 70%)',
          borderRadius: '50%', pointerEvents: 'none',
        }} />

        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ animation: `${slideReveal} 0.7s ease both` }}>
                <Typography
                  variant="overline"
                  sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3, fontSize: '0.85rem', display: 'block', mb: 2 }}
                >
                  Lynn, MA · Professional IT Services
                </Typography>
                <Typography
                  variant="h1"
                  sx={{ fontSize: { xs: '2.4rem', md: '3.8rem' }, fontWeight: 800, lineHeight: 1.15, mb: 3, color: '#fff' }}
                >
                  Enterprise-Grade IT{' '}
                  <Box component="span" className="gradient-text">For Your Business</Box>
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: 'text.secondary', mb: 4, fontWeight: 400, maxWidth: 520, lineHeight: 1.8 }}
                >
                  Hardware repair, cybersecurity, Microsoft 365, networking, and full IT support — backed by 16+ years of enterprise experience.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <RouterLink to="/services" style={{ textDecoration: 'none' }}>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                        color: '#2d3436', fontWeight: 700, px: 4, py: 1.5, fontSize: '1rem',
                        transition: 'all 0.3s ease',
                        '&:hover': { background: 'linear-gradient(135deg, var(--primary) 20%, var(--secondary) 80%)', transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(255,107,107,0.3)' },
                      }}
                    >
                      View Our Services
                    </Button>
                  </RouterLink>
                  <RouterLink to="/booking" style={{ textDecoration: 'none' }}>
                    <Button
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: 'rgba(255,107,107,0.5)', color: '#fff', px: 4, py: 1.5, fontSize: '1rem',
                        transition: 'all 0.3s ease',
                        '&:hover': { borderColor: 'primary.main', background: 'rgba(255,107,107,0.08)' },
                      }}
                    >
                      Book a Repair
                    </Button>
                  </RouterLink>
                </Box>
              </Box>
            </Grid>

            {/* Animated orb graphic */}
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', width: 400, height: 400 }}>
                {[0, 1.2, 2.4].map((delay, i) => (
                  <Box key={`rpl-${i}`} sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    width: 360, height: 360, ml: '-180px', mt: '-180px',
                    borderRadius: '50%', border: '1.5px solid rgba(255,107,107,0.38)',
                    animation: `${rippleOut} 3.6s ease-out infinite`, animationDelay: `${delay}s`,
                    pointerEvents: 'none', zIndex: 0,
                  }} />
                ))}
                <Box sx={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: 420, height: 420, ml: '-210px', mt: '-210px',
                  borderRadius: '50%', border: '1.5px dashed rgba(255,107,107,0.22)',
                  animation: `${rotateCW} 32s linear infinite`, pointerEvents: 'none', zIndex: 1,
                }}>
                  {[
                    { top: '-5px', left: '50%', transform: 'translateX(-50%)' },
                    { bottom: '-5px', left: '50%', transform: 'translateX(-50%)' },
                    { left: '-5px', top: '50%', transform: 'translateY(-50%)' },
                    { right: '-5px', top: '50%', transform: 'translateY(-50%)' },
                  ].map((pos, idx) => (
                    <Box key={idx} sx={{
                      position: 'absolute', ...pos, width: 10, height: 10, borderRadius: '50%',
                      background: 'radial-gradient(circle, #ff6b6b 0%, rgba(255,107,107,0.25) 100%)',
                      animation: `${orbitDotPulse} 2.4s ease-in-out infinite`, animationDelay: `${idx * 0.6}s`,
                    }} />
                  ))}
                </Box>
                <Box sx={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: 360, height: 360, ml: '-180px', mt: '-180px',
                  borderRadius: '50%', overflow: 'hidden', pointerEvents: 'none', zIndex: 1,
                }}>
                  <Box sx={{
                    width: '100%', height: '100%',
                    background: 'conic-gradient(from 0deg, transparent 0deg, rgba(255,107,107,0.14) 50deg, transparent 70deg)',
                    animation: `${rotateCW} 5s linear infinite`,
                  }} />
                </Box>
                <Box sx={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: 360, height: 360, ml: '-180px', mt: '-180px',
                  borderRadius: '50%', overflow: 'hidden', pointerEvents: 'none', zIndex: 1,
                }}>
                  <Box sx={{
                    width: '100%', height: '100%',
                    background: 'conic-gradient(from 180deg, transparent 0deg, rgba(255,217,61,0.09) 40deg, transparent 60deg)',
                    animation: `${rotateCCW} 8s linear infinite`,
                  }} />
                </Box>
                <Box sx={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: 360, height: 360, ml: '-180px', mt: '-180px',
                  borderRadius: '50%', border: '1px solid rgba(255,107,107,0.15)',
                  animation: `${ringPulse} 4s ease-in-out infinite`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
                }}>
                  <Box sx={{
                    width: 270, height: 270, borderRadius: '50%',
                    border: '1px solid rgba(255,107,107,0.09)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                  }}>
                    <Box sx={{
                      position: 'absolute', width: '100%', height: '100%', borderRadius: '50%',
                      border: '1.5px dashed rgba(255,217,61,0.22)',
                      animation: `${rotateCCW} 18s linear infinite`, pointerEvents: 'none',
                    }} />
                    <Box sx={{ position: 'relative', width: 186, height: 186, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{
                        position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                        borderRadius: '50%',
                        background: 'conic-gradient(from 0deg, #ff6b6b, #ffd93d, #6ba3ff, #ff6b6b)',
                        animation: `${rotateCW} 4s linear infinite`,
                        WebkitMaskImage: 'radial-gradient(circle at center, transparent 85px, black 85.5px)',
                        maskImage: 'radial-gradient(circle at center, transparent 85px, black 85.5px)',
                        opacity: 0.8,
                      }} />
                      <Box sx={{
                        width: 170, height: 170, borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(255,107,107,0.22) 0%, rgba(255,217,61,0.1) 100%)',
                        border: '1.5px solid rgba(255,107,107,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        animation: `${glowPulse} 3s ease-in-out infinite, ${scaleBreath} 4s ease-in-out infinite`,
                        position: 'relative', zIndex: 1,
                      }}>
                        <ComputerIcon sx={{ fontSize: 68, color: 'primary.main', animation: `${iconFloat} 3s ease-in-out infinite` }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {[
                  { icon: SecurityIcon, top: '-2%',   right: '4%',   left: undefined,  bottom: undefined, label: 'Cybersecurity',       delay: '0s' },
                  { icon: CloudIcon,    bottom: '10%', right: '-10%', left: undefined,  top: undefined,    label: 'Microsoft 365',       delay: '0.8s' },
                  { icon: RouterIcon,   top: '26%',   left: '-14%',  right: undefined, bottom: undefined, label: 'Network Engineering', delay: '1.6s' },
                  { icon: BuildIcon,    bottom: '2%', left: '10%',   right: undefined, top: undefined,    label: 'Enterprise IT',       delay: '2.4s' },
                  { icon: ComputerIcon, top: '38%',   right: '-14%', left: undefined,  bottom: undefined, label: 'Device Repair',       delay: '3.2s' },
                ].map((badge, i) => (
                  <Box key={i} sx={{
                    position: 'absolute',
                    top: badge.top, bottom: badge.bottom, left: badge.left, right: badge.right,
                    background: 'rgba(30,36,38,0.96)', border: '1px solid rgba(255,107,107,0.28)',
                    borderRadius: '12px', px: 1.6, py: 0.9,
                    display: 'flex', alignItems: 'center', gap: 0.8, zIndex: 10,
                    animation: `${floatBadge} 3.2s ease-in-out infinite, ${badgeGlow} 3.2s ease-in-out infinite`,
                    animationDelay: `${badge.delay}, ${badge.delay}`,
                  }}>
                    {React.createElement(badge.icon, { sx: { fontSize: 15, color: 'primary.main' } })}
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.72rem', letterSpacing: 0.5 }}>
                      {badge.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Ticker / Trust marquee ── */}
      <Box sx={{ py: 2, background: 'rgba(255,107,107,0.04)', borderTop: '1px solid rgba(255,107,107,0.1)', borderBottom: '1px solid rgba(255,107,107,0.1)', overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', width: 'max-content',
          animation: `${marqueeTicker} 28s linear infinite`,
          '&:hover': { animationPlayState: 'paused' },
        }}>
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: '#ff6b6b', mr: 2, flexShrink: 0 }} />
              <Typography sx={{ color: 'rgba(255,232,232,0.65)', fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Animated Stats Bar ── */}
      <Box ref={statsRef} sx={{ background: 'rgba(255,107,107,0.03)', py: 6 }}>
        <Container>
          <Grid container spacing={2} justifyContent="center">
            {stats.map((stat, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <StatCard {...stat} started={statsInView} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── What We Offer ── */}
      <Box sx={{ py: 10 }}>
        <Container>
          <Reveal>
            <Typography variant="h2" align="center" className="gradient-text" sx={{ mb: 2, fontWeight: 700 }}>
              What We Offer
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: 600, mx: 'auto' }}>
              Enterprise-grade IT services for homes and small businesses
            </Typography>
          </Reveal>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Reveal delay={index * 0.08}>
                  <Card sx={{
                    height: '100%',
                    background: 'rgba(255,107,107,0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255,107,107,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      border: '1px solid rgba(255,107,107,0.28)',
                      boxShadow: '0 12px 30px rgba(255,107,107,0.12)',
                    },
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ width: 52, height: 52, borderRadius: '12px', background: 'rgba(255,107,107,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        {React.createElement(feature.icon, { sx: { fontSize: 26, color: 'primary.main' } })}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{feature.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>{feature.description}</Typography>
                    </CardContent>
                  </Card>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How It Works ── */}
      <Box sx={{ py: 10, background: 'rgba(255,107,107,0.02)', borderTop: '1px solid rgba(255,107,107,0.06)', borderBottom: '1px solid rgba(255,107,107,0.06)' }}>
        <Container>
          <Reveal>
            <Typography variant="h2" align="center" className="gradient-text" sx={{ mb: 2, fontWeight: 700 }}>
              How It Works
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: 560, mx: 'auto' }}>
              Getting your device fixed or IT sorted is simple — four easy steps
            </Typography>
          </Reveal>
          <Grid container spacing={3}>
            {howItWorks.map((step, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Reveal delay={i * 0.1}>
                  <Box sx={{
                    p: 3.5, borderRadius: '18px',
                    background: 'rgba(255,107,107,0.05)',
                    border: '1px solid rgba(255,107,107,0.1)',
                    position: 'relative', height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-6px)', border: '1px solid rgba(255,107,107,0.28)', boxShadow: '0 12px 30px rgba(255,107,107,0.12)' },
                  }}>
                    <Typography sx={{
                      position: 'absolute', top: 16, right: 20,
                      fontSize: '2.2rem', fontWeight: 900, lineHeight: 1,
                      color: 'rgba(255,107,107,0.12)', letterSpacing: '-0.03em',
                    }}>
                      {step.step}
                    </Typography>
                    <Box sx={{ width: 56, height: 56, borderRadius: '14px', background: 'linear-gradient(135deg, rgba(255,107,107,0.15) 0%, rgba(255,217,61,0.08) 100%)', border: '1px solid rgba(255,107,107,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                      {React.createElement(step.icon, { sx: { fontSize: 28, color: 'primary.main' } })}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#fff' }}>{step.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>{step.description}</Typography>
                  </Box>
                </Reveal>
              </Grid>
            ))}
          </Grid>
          <Reveal delay={0.3}>
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <RouterLink to="/booking" style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                    color: '#2d3436', fontWeight: 700, px: 5, py: 1.5,
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(255,107,107,0.3)' },
                  }}
                >
                  Book Your Service Now
                </Button>
              </RouterLink>
            </Box>
          </Reveal>
        </Container>
      </Box>

      {/* ── Why Choose Us ── */}
      <Box sx={{ py: 10 }}>
        <Container>
          <Reveal>
            <Typography variant="h2" align="center" className="gradient-text" sx={{ mb: 2, fontWeight: 700 }}>
              Why Choose Us
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: 600, mx: 'auto' }}>
              Real expertise, real results — no outsourcing, no middleman
            </Typography>
          </Reveal>
          <Grid container spacing={3}>
            {whyChooseUs.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Reveal delay={index * 0.08}>
                  <RouterLink to={item.link} style={{ textDecoration: 'none' }}>
                    <Box sx={{
                      height: '100%', p: 3, borderRadius: '16px',
                      background: 'rgba(255,107,107,0.06)',
                      border: '1px solid rgba(255,107,107,0.1)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease', cursor: 'pointer',
                      '&:hover': { transform: 'translateY(-6px)', background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.28)', boxShadow: '0 12px 30px rgba(255,107,107,0.12)' },
                    }}>
                      <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,107,107,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, mx: 'auto' }}>
                        {React.createElement(item.icon, { sx: { fontSize: 30, color: 'primary.main' } })}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff', textAlign: 'center', mb: 1 }}>{item.title}</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', textAlign: 'center', lineHeight: 1.75 }}>{item.description}</Typography>
                    </Box>
                  </RouterLink>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Certifications bar ── */}
      <Box sx={{ py: 5, borderTop: '1px solid rgba(255,107,107,0.07)', borderBottom: '1px solid rgba(255,107,107,0.07)' }}>
        <Container>
          <Reveal>
            <Typography variant="overline" align="center" sx={{ color: 'text.secondary', letterSpacing: 3, display: 'block', mb: 4 }}>
              Certifications & Expertise
            </Typography>
          </Reveal>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
            {[
              { label: 'CompTIA CySA+', sub: 'Cybersecurity Analyst' },
              { label: 'Microsoft 365',  sub: 'Administration' },
              { label: 'Azure / Entra ID', sub: 'Cloud & Identity' },
              { label: 'Cisco ISE / NAC', sub: 'Zero Trust Security' },
              { label: 'VMware / Proxmox', sub: 'Virtualization' },
              { label: '16+ Years',      sub: 'Enterprise Experience' },
            ].map((cert, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <Box sx={{
                  px: 3, py: 1.5,
                  background: 'rgba(255,107,107,0.06)',
                  border: '1px solid rgba(255,107,107,0.15)',
                  borderRadius: '12px', textAlign: 'center', minWidth: 140,
                  transition: 'all 0.25s ease',
                  '&:hover': { background: 'rgba(255,107,107,0.1)', transform: 'translateY(-3px)', boxShadow: '0 6px 16px rgba(255,107,107,0.1)' },
                }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>{cert.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{cert.sub}</Typography>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Testimonials ── */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="h2" align="center" className="gradient-text" sx={{ mb: 2, fontWeight: 700 }}>
              What Clients Say
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: 560, mx: 'auto' }}>
              Real feedback from real customers in Lynn, MA and beyond.
            </Typography>
          </Reveal>
          <Grid container spacing={3}>
            {reviews.map((review, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Reveal delay={i * 0.07}>
                  <Card sx={{
                    height: '100%',
                    background: 'rgba(255,107,107,0.05)',
                    border: '1px solid rgba(255,107,107,0.1)',
                    borderRadius: '16px',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)', border: '1px solid rgba(255,107,107,0.25)', boxShadow: '0 10px 28px rgba(255,107,107,0.1)' },
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', gap: 0.3, mb: 1.5 }}>
                        {Array(review.rating).fill(0).map((_, s) => (
                          <StarIcon key={s} sx={{ fontSize: '1rem', color: '#ffd93d' }} />
                        ))}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2.5, fontStyle: 'italic' }}>
                        "{review.text}"
                      </Typography>
                      <Box sx={{ borderTop: '1px solid rgba(255,107,107,0.08)', pt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #ff6b6b, #ffd93d)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem', color: '#1a1f21', flexShrink: 0 }}>
                          {review.name[0]}
                        </Box>
                        <Box>
                          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>{review.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{review.location}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Reveal>
            <Box sx={{
              textAlign: 'center',
              p: { xs: 4, md: 8 },
              borderRadius: '24px',
              background: 'linear-gradient(135deg, rgba(255,107,107,0.08) 0%, rgba(255,217,61,0.04) 100%)',
              border: '1px solid rgba(255,107,107,0.18)',
              backdropFilter: 'blur(12px)',
              position: 'relative', overflow: 'hidden',
            }}>
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,107,107,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 150, height: 150, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,217,61,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
              <Chip label="Fast Response · Fair Pricing · Expert Work" size="small" sx={{ mb: 3, background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)', color: 'rgba(255,232,232,0.8)', fontSize: '0.78rem' }} />
              <Typography variant="h3" className="gradient-text" sx={{ mb: 2, fontWeight: 700 }}>
                Ready to Get Started?
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto', lineHeight: 1.7 }}>
                Book your repair or IT service today — fast response, expert work, fair pricing.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <RouterLink to="/booking" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                      color: '#2d3436', fontWeight: 700, px: 4, py: 1.5,
                      transition: 'all 0.3s ease',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(255,107,107,0.3)' },
                    }}
                  >
                    Book a Service
                  </Button>
                </RouterLink>
                <RouterLink to="/contact" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: 'rgba(255,107,107,0.5)', color: '#fff', px: 4, py: 1.5,
                      transition: 'all 0.3s ease',
                      '&:hover': { borderColor: 'primary.main', background: 'rgba(255,107,107,0.08)' },
                    }}
                  >
                    Contact Us
                  </Button>
                </RouterLink>
              </Box>
            </Box>
          </Reveal>
        </Container>
      </Box>

    </Box>
  );
}

export default Home;
